<template>
  <v-row no-gutters>
    <v-col>
      <v-row no-gutters class="pb-4">
        <v-col cols="8">
          <v-row no-gutters align="baseline">
            <v-col cols="auto" class="pr-4 font-weight-bold">
              {{ $i18n.t('admin.external_systems_table.connections') }}
            </v-col>
            <v-col cols="auto">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('button.addConnection')"
                @click="openSystemEditAddDialog(undefined)"
              />
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <data-input
                :data-type="dataTypeText"
                prepend-inner-icon="mdi-magnify"
                :placeholder="$i18n.t('default.find')"
                clearable
                hide-details
                v-model="search"
                class="align-center justify-center"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="8" style="border: 1px solid rgba(0, 0, 0, 0.12);">
          <v-data-table
            dense
            :search="search"
            calculate-widths
            :footer-props="{
              itemsPerPageOptions: [10, 20, 50, 100],
            }"
            :items="externalSystems"
            :headers="headers"
          >
            <template #header.system_id="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.system_id`) }}
              </v-tooltip>
            </template>
            <template #header.source_field="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.source_field`) }}
              </v-tooltip>
            </template>
            <template #[`header.${attributeIdName}`]="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 16px" color="info">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t(`admin.external_systems_table.tooltips.${attributeIdName}`) }}
              </v-tooltip>
            </template>
            <template #item.actions="{item}">
              <dots-menu
                :items="dotsItems"
                @edit="openSystemEditAddDialog(item)"
                @delete="openDeleteDialog(item)"
                @refreshView="handleConnectionRefresh(item.id)"
                :disabled="dotsMenuDisabled"
              ></dots-menu>
            </template>
            <template #item.datasource="{item}">
              {{ getDataSourceByName(item) }}
            </template>
            <template #[`item.${attributeIdName}`]="{item}">
              {{ getDataSourceRelationByName(item) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="isSystemDialogLoaded"
        :is-visible.sync="isSystemDialogVisible"
        :inputs="systemDialogInputs"
        :agree-text="agreeText"
        dialog-width="40%"
        :title-translation="systemDialogName"
        @modalClosed="onClose"
        :loading="isConfirmButtonLoading"
        @agree="computedAgreeAction"
      >
        <template #datasource.input="slot">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="datasource" v-bind="slot"> </data-input>
            </v-col>
          </v-row>
        </template>
        <template #[`${attributeIdName}.input`]="slot">
          <v-row no-gutters>
            <v-col cols="9">
              <data-input v-model="attributeId" v-bind="slot" :items="selectedDatasourceAttributes"> </data-input>
            </v-col>
          </v-row>
        </template>
      </main-input-dialog>
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteConnection')"
        :body="$i18n.t('dialog.body.deleteConnectionConfirmation')"
        @agree="handleConnectionDelete"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeDelete"
        @modalClosed="onClose"
      />
    </v-col>
  </v-row>
</template>

<script>
import BaseRoundedButton from '@/components/BaseRoundedButton';
import DataInput from '@/components/DataInput';
import { get } from 'vuex-pathify';
import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'ExternalSystemsTable',
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    DotsMenu,
    DataInput,
    BaseRoundedButton,
  },
  props: {
    attributeIdName: {
      type: String,
      default: 'attribute_id',
    },
    inputs: {
      type: Array,
      default: () => {
        return [];
      },
    },
    externalSystems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    availableSystems: {
      type: Array,
      default: () => {
        return [];
      },
    },
    initGets: {
      type: Function,
      required: true,
    },
    deleteConnection: {
      type: Function,
      required: true,
    },
    editConnection: {
      type: Function,
      required: true,
    },
    addConnection: {
      type: Function,
      required: true,
    },
    refreshView: {
      type: Function,
    },
  },
  data: () => ({
    search: '',
    systemDialogInputs: undefined,
    editingSystem: undefined,
    datasource: undefined,
    attributeId: undefined,
    isSystemDialogVisible: false,
    isSystemDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isConfirmButtonLoading: false,
    dataTypeText: {
      name: 'text',
    },
    dotsMenuDisabled: false,
  }),
  computed: {
    dataSources: get('layers/metadata'),
    dotsItems() {
      return [{ name: 'edit' }, { name: 'delete' }, ...(this.refreshView ? [{ name: 'refreshView' }] : [])];
    },
    selectedDatasourceAttributes() {
      return this.datasource ? this.selectedDatasource.attributes_schema.attributes : undefined;
    },
    selectedDatasource() {
      return this.dataSources[this.datasource];
    },
    agreeText() {
      return this.editingSystem === undefined ? 'dialog.agreeAdd' : 'dialog.agreeSave';
    },
    systemDialogName() {
      return this.editingSystem === undefined
        ? this.$i18n.t('dialog.title.addConnection')
        : this.$i18n.t('dialog.title.editConnection');
    },
    computedAgreeAction() {
      return this.editingSystem === undefined ? this.handleConnectionAdd : this.handleConnectionEdit;
    },
    headers() {
      return [
        ...this.inputs
          .filter(i => i !== 'password')
          .map(value => {
            return {
              text: this.$i18n.t(`admin.external_systems_table.headers.${value}`),
              value: value,
            };
          }),
        {
          text: this.$i18n.t('admin.zdmStreetLights.tableHeaders.options'),
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '50px',
        },
      ];
    },
    sortedDataSources() {
      const sources = this.$_objectToArray(this.dataSources).filter(
        dataSource => dataSource.scope !== 'module' && dataSource.scope !== 'dxf' && dataSource.scope !== null
      );
      return this.$_sortObjectsByAttribute([...sources], 'verbose_name');
    },
  },
  methods: {
    dialogInputs() {
      return this.inputs.map(input => {
        return {
          name: input,
          straightTitle: this.$i18n.t(`admin.external_systems_table.headers.${input}`),
          dataType: this.getDataType(input),
          items: this.getItems(input),
          value: this.editingSystem ? this.editingSystem[input] : null,
          clearable: true,
          autocomplete: 'off',
          cols: '9',
          ...(input === 'password' && { type: 'password' }),
          ...((input === 'datasource' || input === this.attributeIdName) && { itemValue: this.getItemValue(input) }),
          ...((input === 'datasource' || input === this.attributeIdName) && { itemText: 'verbose_name' }),
          rules: [v => !!v || v == '0' || 'rules.required'],
        };
      });
    },

    getDataType(value) {
      if (value === 'system' || value === 'datasource' || value === this.attributeIdName) {
        return {
          name: 'select',
        };
      } else if (value === 'port') {
        return {
          name: 'integer',
        };
      } else {
        return {
          name: 'text',
        };
      }
    },
    getDataSourceByName(value) {
      const result = this.$_objectToArray(this.dataSources).find(d => d.name === value.datasource);
      return result.verbose_name;
    },
    getDataSourceRelationByName(value) {
      const { datasource } = value;
      const attributeId = value[this.attributeIdName];
      const r = this.dataSources[datasource].attributes_schema.attributes.find(a => a.id === attributeId);
      return r ? r.verbose_name : attributeId;
    },
    getItems(value) {
      if (value === 'system') {
        return this.availableSystems;
      } else if (value === 'datasource') {
        return this.sortedDataSources;
      } else if (value === this.attributeIdName) {
        return this.selectedDatasourceAttributes;
      } else {
        return [];
      }
    },
    getItemValue(value) {
      if (value === 'datasource') {
        return 'name';
      } else {
        return 'id';
      }
    },
    async handleConnectionRefresh(id) {
      this.dotsMenuDisabled = true;
      try {
        await this.refreshView(id);
        this.dotsMenuDisabled = false;
      } catch {
        this.dotsMenuDisabled = false;
      }
    },
    async handleConnectionDelete() {
      this.isConfirmButtonLoading = true;
      try {
        await this.deleteConnection(this.editingSystem.id);
        this.isDeleteDialogVisible = false;
      } finally {
        await this.onClose();
      }
    },
    async handleConnectionAdd() {
      this.isConfirmButtonLoading = true;
      const body = this.systemDialogInputs.reduce((obj, item) => ({ ...obj, [item.name]: item.value }), {});
      body.datasource = this.datasource;
      body[this.attributeIdName] = this.attributeId;
      try {
        await this.addConnection(body);
        this.isSystemDialogVisible = false;
        this.isSystemDialogLoaded = false;
      } finally {
        this.onClose();
      }
    },
    async handleConnectionEdit() {
      this.isConfirmButtonLoading = true;
      const id = this.editingSystem.id;
      const body = this.systemDialogInputs.reduce((obj, item) => ({ ...obj, [item.name]: item.value }), {});
      body.datasource = this.datasource;
      body[this.attributeIdName] = this.attributeId;
      try {
        await this.editConnection({ body, id });
        this.isSystemDialogVisible = false;
        this.isSystemDialogLoaded = false;
      } finally {
        this.onClose();
      }
    },
    onClose() {
      this.editingSystem = undefined;
      this.datasource = undefined;
      this.attributeId = undefined;
      this.isConfirmButtonLoading = false;
    },
    openSystemEditAddDialog(value) {
      if (value) {
        this.editingSystem = value;
        this.datasource = this.editingSystem['datasource'];
        this.attributeId = this.editingSystem[this.attributeIdName];
      }
      this.systemDialogInputs = this.dialogInputs();
      this.isSystemDialogVisible = true;
      this.isSystemDialogLoaded = true;
    },
    openDeleteDialog(value) {
      this.editingSystem = value;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    async init() {
      await this.initGets();
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  table > tbody > tr > td:last-child,
  table > thead > tr > th:last-child {
    position: sticky !important;
    position: -webkit-sticky !important;
    right: 0;
    z-index: 2;
    background: white;
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .v-data-table-header th {
    white-space: nowrap;
  }
}
</style>
